import type { ActionRequest, ActionResult, MemoryDataSourceOptions } from '@fieldia/core';
import type { PageSource } from '../lazy-page';
import { lane as accounting } from './accounting';
import { lane as crm } from './crm';
import type { RealAround, RealLane } from './lane';
import { lane as legal } from './legal';
import { lane as operations } from './operations';
import { lane as people } from './people';
import { lane as sales } from './sales';

const LANES: RealLane[] = [sales, accounting, crm, people, operations, legal];

/** Maps by model then key, put together: a later lane adds to a model, never replaces one. */
function byModel<T>(pick: (lane: RealLane) => Record<string, Record<string, T>> | undefined): Record<string, Record<string, T>> {
  const all: Record<string, Record<string, T>> = {};
  for (const lane of LANES) {
    for (const [model, rows] of Object.entries(pick(lane) ?? {})) {
      const merged: Record<string, T> = { ...all[model] };
      // Two lanes listing a model's fields (the lines under a link) each keep theirs: the lists join, each field once.
      for (const [key, value] of Object.entries(rows)) {
        const had = merged[key];
        merged[key] = (Array.isArray(had) && Array.isArray(value) ? [...new Set([...had, ...value])] : value) as T;
      }
      all[model] = merged;
    }
  }
  return all;
}

/** Every lane's real pages, records and answers, as the demos take them. */
export const real = {
  pages: Object.assign({}, ...LANES.map((lane) => lane.pages)) as Record<string, PageSource>,
  opened: Object.assign({}, ...LANES.map((lane) => lane.opened ?? {})) as Record<string, PageSource>,
  related: Object.assign({}, ...LANES.map((lane) => lane.related ?? {})) as Record<string, PageSource>,
  records: byModel((lane) => lane.records) as NonNullable<MemoryDataSourceOptions['records']>,
  onchange: byModel((lane) => lane.onchange) as NonNullable<MemoryDataSourceOptions['onchange']>,
  warnings: byModel((lane) => lane.warnings) as NonNullable<MemoryDataSourceOptions['warnings']>,
  lists: Object.assign({}, ...LANES.map((lane) => lane.lists ?? {})) as NonNullable<MemoryDataSourceOptions['lists']>,
  labelField: Object.assign({}, ...LANES.map((lane) => lane.labelField ?? {})) as NonNullable<MemoryDataSourceOptions['labelField']>,
  definitions: Object.assign({}, ...LANES.map((lane) => lane.definitions ?? {})) as NonNullable<MemoryDataSourceOptions['definitions']>,
  /** What links show of each model's records: a lane adds to a model's, a later one's word winning. */
  shows: byModel((lane) => lane.shows as Record<string, Record<string, unknown>> | undefined) as NonNullable<MemoryDataSourceOptions['shows']>,
  attachments: Object.assign({}, ...LANES.map((lane) => lane.attachments ?? {})) as NonNullable<MemoryDataSourceOptions['attachments']>,
  /** By the `?page=` name: the person using it, the pager's records and the breadcrumbs — as `around`, or as `users` and `navigation`. */
  around: Object.assign({}, ...LANES.map((lane) => lane.around ?? {})) as Record<string, RealAround>,
  users: Object.assign({}, ...LANES.map((lane) => lane.users ?? {})) as NonNullable<RealLane['users']>,
  navigation: Object.assign({}, ...LANES.map((lane) => lane.navigation ?? {})) as NonNullable<RealLane['navigation']>,
  /**
   * The first lane that knows the action answers it. A lane may answer later,
   * and "later, nothing" is not knowing it either: the next lane is asked.
   */
  async action(request: ActionRequest, locale?: string): Promise<ActionResult | undefined> {
    for (const lane of LANES) {
      const answer = await lane.action?.(request, locale);
      if (answer !== undefined) return answer;
    }
    return undefined;
  },
};
